/* eslint-disable */
import React, { RefObject, useRef, useState, useEffect } from 'react';
import { ThemeProvider } from '@ldm/theme';
import HierarchyList from './HierarchyList';
import { Filter, HierarchyItem, LevelDataMap } from './types';
import { getLevelDataMaps, hasAncestor, hasItem } from './util';

const getArrayWithMaps = (maxLevels) =>
	Array(maxLevels)
		.fill(0)
		.map(() => new Map());

const setOrDelete = (
	setOrDeleteToggle: boolean,
	mapRef: RefObject<LevelDataMap[]>,
	item: HierarchyItem
) => {
	const map = mapRef.current?.[item?.level];
	if (!map) return;
	if (setOrDeleteToggle) {
		map.set(item.nestedId, item);
	} else {
		map.delete(item.nestedId);
	}
};

export type HierarchySelectorProps = {
	/**
	 * An array of nodes
	 */
	data: HierarchySelectorItem[];
	/**
	 * Max number of levels
	 *
	 * @default 5
	 */
	maxLevels?: number;
	/**
	 * Initial selection
	 */
	selections: Filter[];
	/**
	 * On change handler
	 */
	onSelectionChange: (selection: Filter[]) => void;
	/**
	 * noRollup
	 *
	 * @default false
	 */
	noRollup?: boolean;
	/**
	 * Header strings
	 */
	levelMetaData: { label: string }[];
};

/**
 * @component
 * @example ../README_HS.md
 */
const HierarchySelector = (props: HierarchySelectorProps) => {
	const {
		data,
		maxLevels = 5,
		onSelectionChange,
		selections,
		noRollup = false,
		levelMetaData,
		...rest
	} = props;
	const [levelDataMaps, setLevelDataMaps] = useState<LevelDataMap[]>([]);

	// State used to trigger rerenders, since we are passing refs as props
	// and updating the values in refs won't trigger rerenders
	const [renderCounter, setRenderCounter] = useState(0);

	// Items that display in the next-level-down HierarchyList.
	// Note: this does not determine child item visibility, expandedItemsRef does.
	const focusedItemsRef = useRef<LevelDataMap[]>(getArrayWithMaps(maxLevels));

	// Items with visible children in the next-level-down HierarchyList
	const expandedItemsRef = useRef<LevelDataMap[]>(getArrayWithMaps(maxLevels));

	// Items that are selected.
	// Note: this does not determine visibility in the next-level-down HierarchyList.
	const selectedItemsRef = useRef<LevelDataMap[]>(getArrayWithMaps(maxLevels));

	// Items with descendants that are both selected and unselected
	const indeterminateItemsRef = useRef<LevelDataMap[]>(
		getArrayWithMaps(maxLevels)
	);

	const rerender = () => {
		setRenderCounter(renderCounter + 1);
	};

	const select = (item) => {
		setOrDelete(true, selectedItemsRef, item);
	};

	const deselect = (item) => {
		setOrDelete(false, selectedItemsRef, item);
	};

	const focus = (item) => {
		setOrDelete(true, focusedItemsRef, item);
	};

	const defocus = (item) => {
		setOrDelete(false, focusedItemsRef, item);
	};

	const collapse = (item) => {
		setOrDelete(false, expandedItemsRef, item);
	};

	const expand = (item) => {
		setOrDelete(true, expandedItemsRef, item);
	};

	const removeIndeterminate = (item) => {
		setOrDelete(false, indeterminateItemsRef, item);
	};

	const getAreDescendantsSelected = (item: HierarchyItem) => {
		const children = item.subHierarchy;
		const selectedChildren = children.filter((child) =>
			hasItem(selectedItemsRef, child)
		);
		const indeterminateChildren = children.filter((child) =>
			hasItem(indeterminateItemsRef, child)
		);
		return selectedChildren.length > 0 || indeterminateChildren.length > 0;
	};

	const handleParentItemsSelection = (
		item: HierarchyItem,
		checked: boolean
	) => {
		if (item.level < 1 || !item.parentNestedId) return;
		const parentLevel = item.level - 1;
		const parentItem = levelDataMaps[parentLevel]?.get(item.parentNestedId);
		if (!parentItem) return;
		const children = parentItem.subHierarchy;
		const siblings = children.filter(
			(child) => child.nestedId !== item.nestedId
		);

		const selectedChildren = children.filter((child) =>
			hasItem(selectedItemsRef, child)
		);
		const allChildrenSelected = selectedChildren.length === children.length;

		// When noRollup = false and a parent is selected, children display as checked
		// even though the children are not selected (the selections roll up).
		if (!noRollup) {
			const isAncestorSelected = hasAncestor(
				selectedItemsRef,
				item,
				levelDataMaps
			);

			// If an ancestor was previously selected and the child was deselected,
			// select all of the siblings
			if (!checked && isAncestorSelected) {
				siblings.forEach(select);
			}

			// If all children are now selected, select the parent and deselect the children
			if (allChildrenSelected) {
				select(parentItem);
				children.forEach(deselect);
			} else {
				deselect(parentItem);
			}

			const areDescendantsSelected = getAreDescendantsSelected(parentItem);
			const someChildrenSelected =
				areDescendantsSelected && !allChildrenSelected;

			// When some, but not all, children are selected, the parent is indeterminate
			setOrDelete(someChildrenSelected, indeterminateItemsRef, parentItem);
		} else {
			// make the parent indeterminate if it isn't selected and some or all children are selected
			const areDescendantsSelected = getAreDescendantsSelected(parentItem);
			const isParentSelected = hasItem(selectedItemsRef, parentItem);
			setOrDelete(
				!isParentSelected && areDescendantsSelected,
				indeterminateItemsRef,
				parentItem
			);
		}

		const isParentFocused = hasItem(focusedItemsRef, parentItem) || checked;
		setOrDelete(isParentFocused, focusedItemsRef, parentItem);

		const isExpanded =
			hasItem(expandedItemsRef, parentItem) ||
			hasItem(selectedItemsRef, item) ||
			isParentFocused;

		setOrDelete(isExpanded, expandedItemsRef, parentItem);

		handleParentItemsSelection(parentItem, checked);
	};

	// Remove all descendants from the selected and indeterminate items
	const deselectDescendants = (item) => {
		item.subHierarchy?.forEach((childItem) => {
			deselect(childItem);
			removeIndeterminate(childItem);
			deselectDescendants(childItem);
		});
	};

	// Return an Array of all the selected values
	const getSelections = (): Filter[] =>
		selectedItemsRef.current.flatMap((map) =>
			Array.from(map.values()).map(({ nestedId, name, level }) => ({
				nestedId,
				name,
				level,
			}))
		);

	const clearMapsBelowLevel = (mapArray, level) => {
		mapArray.forEach((levelMap, i) => {
			if (i >= level) {
				mapArray[i] = new Map();
			}
		});
	};

	const clearFocusedItemsBelowLevel = (level) =>
		clearMapsBelowLevel(focusedItemsRef.current, level);

	const clearIndeterminateItemsBelowLevel = (level) =>
		clearMapsBelowLevel(indeterminateItemsRef.current, level);

	const handleSelectionChange = (
		level,
		itemNestedId,
		checked,
		topLevel = undefined
	) => {
		const currentItem = levelDataMaps[level]?.get(itemNestedId);
		if (!currentItem || currentItem.disabled) return;

		setOrDelete(checked, selectedItemsRef, currentItem);
		// TODO: currentItem.expanded = checked || (noRollup && currentItem.expanded);
		setOrDelete(checked, expandedItemsRef, currentItem);
		// TODO: currentItem.showChildren = checked || (noRollup && currentItem.showChildren);

		// When rolling up, a selected or deselected item can't be indeterminate.
		// When not rolling up, an item can't be indeterminate if it was selected,
		// but could be if it was deselected and it has selected children.
		if (!noRollup || checked) {
			removeIndeterminate(currentItem);
		}

		if (!noRollup) {
			// When rolling up, all descendants should be deselected after an item is
			// either selected or deselected
			deselectDescendants(currentItem);
		} else {
			const isIndeterminate =
				!checked && getAreDescendantsSelected(currentItem);
			setOrDelete(isIndeterminate, indeterminateItemsRef, currentItem);
		}

		handleParentItemsSelection(currentItem, checked);

		clearFocusedItemsBelowLevel(topLevel);

		if (checked) {
			focus(currentItem);
		}

		// Call the onSelectionChange function from props, if the level matches
		if (Number(level) === Number(topLevel)) {
			onSelectionChange(getSelections());
		}

		rerender();
	};

	const handleOnExpand = (itemLevel, itemNestedId) => {
		const currentItem = levelDataMaps[itemLevel]?.get(itemNestedId);
		if (!currentItem) return;

		const isFocused = !focusedItemsRef.current[itemLevel].has(itemNestedId);

		if (isFocused) {
			levelDataMaps[itemLevel].forEach((sibling) => {
				const isSiblingSelected = hasItem(selectedItemsRef, sibling);
				if (!isSiblingSelected) {
					// Hide siblings that aren't selected
					defocus(sibling);
				}
				// Collapse all siblings
				collapse(sibling);
			});
		}
		clearFocusedItemsBelowLevel(itemLevel);
		setOrDelete(isFocused, focusedItemsRef, currentItem);
		setOrDelete(isFocused, expandedItemsRef, currentItem);

		rerender();
	};

	const getVisibleItems = (level: number) => {
		if (level === 0) {
			return levelDataMaps[level];
		}
		const parentLevel = level - 1;
		const parentItems = [
			...selectedItemsRef.current[parentLevel]?.values(),
			...focusedItemsRef.current[parentLevel]?.values(),
			...indeterminateItemsRef.current[parentLevel].values(),
		]
			// Filter out parent items without children, since they won't be rendered in the child menu
			.filter((pItem) => pItem.subHierarchy.length > 0);
		return parentItems.flatMap((item) => item.subHierarchy);
	};

	const handleSelectAll = (level, checked) => {
		if (!noRollup) {
			if (level === 0) {
				levelDataMaps[0].forEach((item) => {
					if (item.disabled) return;
					if (checked) {
						select(item);
					} else {
						deselect(item);
					}
					deselectDescendants(item);
				});
			} else {
				const parentLevel = level - 1;
				// Select or deselect all focused parents
				focusedItemsRef.current[parentLevel].forEach((pItem) => {
					handleSelectionChange(pItem.level, pItem.nestedId, checked);
					expand(pItem);
				});
				// Select or deselect all selected parents
				selectedItemsRef.current[parentLevel].forEach((pItem) => {
					handleSelectionChange(pItem.level, pItem.nestedId, checked);
					focus(pItem);
					expand(pItem);
				});
				// Select or deselect all indeterminate parents
				indeterminateItemsRef.current[parentLevel].forEach((pItem) => {
					handleSelectionChange(pItem.level, pItem.nestedId, checked);
					focus(pItem);
					expand(pItem);
				});
			}
			clearFocusedItemsBelowLevel(level);
			clearIndeterminateItemsBelowLevel(level);
		} else {
			getVisibleItems(level).forEach((item) => {
				if (item.disabled) return;
				setOrDelete(checked, selectedItemsRef, item);
				if (checked) {
					removeIndeterminate(item);
				} else {
					setOrDelete(
						getAreDescendantsSelected(item),
						indeterminateItemsRef,
						item
					);
					handleParentItemsSelection(item, checked);
				}
				const lvl = level === 0 ? 0 : level - 1;
				const parentItem = levelDataMaps[lvl]?.get(item.parentNestedId ?? '');
				// focus and expand parent items so they stay visible in the child menu
				focus(parentItem);
				expand(parentItem);
			});
		}
		onSelectionChange(getSelections());
		rerender();
	};

	const handleSubHierarchyExpandCollapse = (
		parentLevel: number,
		parentNestedId: string
	) => {
		const parentItem = levelDataMaps[parentLevel]?.get(parentNestedId);
		if (!parentItem) return;
		const isParentExpanded = hasItem(expandedItemsRef, parentItem);
		setOrDelete(!isParentExpanded, expandedItemsRef, parentItem);
		rerender();
	};

	const restoreOriginalSelections = () => {
		if (!levelDataMaps || !selections?.length) return;

		selections.forEach((sel) => {
			const { level, nestedId } = sel;
			if (level === undefined) return;
			const item = levelDataMaps[level]?.get(nestedId);
			if (item && !hasItem(selectedItemsRef, item)) {
				handleSelectionChange(level, nestedId, true);
			}
		});
	};

	useEffect(() => {
		const newLevelDataMaps = getLevelDataMaps(data, []);
		setLevelDataMaps(newLevelDataMaps ?? []);
		restoreOriginalSelections();
	}, [data, JSON.stringify(selections)]);

	useEffect(restoreOriginalSelections, [levelDataMaps]);

	const renderLevelDataMap = (level) => {
		return (
			<HierarchyList
				levelDataMaps={levelDataMaps}
				level={level}
				indeterminateItems={indeterminateItemsRef.current}
				focusedItems={focusedItemsRef.current}
				selectedItems={selectedItemsRef.current}
				expandedItems={expandedItemsRef.current}
				header={levelMetaData[level].label}
				noRollup={noRollup}
				onSelectAll={(checked) => handleSelectAll(level, checked)}
				onSelectionChange={(lvl, itemNestedId, checked) => {
					handleSelectionChange(lvl, itemNestedId, checked, level);
				}}
				onExpand={handleOnExpand}
				onSubHierarchyExpandCollapse={handleSubHierarchyExpandCollapse}
			/>
		);
	};

	return (
		<ThemeProvider>
			<div className={'mapsContainer'} {...rest}>
				{(levelDataMaps || [])
					.slice(0, maxLevels)
					?.map((levelDataMap, level) => {
						return (
							<div
								className={'mapContainer'}
								// eslint-disable-next-line react/no-array-index-key
								key={`level-data-container-${level}`}>
								{renderLevelDataMap(level)}
							</div>
						);
					})}
			</div>
		</ThemeProvider>
	);
};

HierarchySelector.displayName = 'HierarchySelector';

export default HierarchySelector;
